import { useCallback, useEffect } from 'react';
import { useUIStore, useSettingsStore } from '@/stores';
import './SettingsModal.css';

/**
 * SettingsModal - 設定モーダル
 * ゲーム設定の表示と変更を提供
 */
const SettingsModal = () => {
  const isOpen = useUIStore((state) => state.modals.settings.isOpen);
  const closeSettings = useUIStore((state) => state.closeSettings);

  // 設定値
  const player = useSettingsStore((state) => state.player);
  const audio = useSettingsStore((state) => state.audio);
  const visual = useSettingsStore((state) => state.visual);
  const rules = useSettingsStore((state) => state.rules);

  // 設定更新アクション
  const updatePlayerName = useSettingsStore((state) => state.updatePlayerName);
  const updateAudio = useSettingsStore((state) => state.updateAudio);
  const updateVisual = useSettingsStore((state) => state.updateVisual);
  const updateRules = useSettingsStore((state) => state.updateRules);
  const resetStats = useSettingsStore((state) => state.resetStats);
  const resetToDefaults = useSettingsStore((state) => state.resetToDefaults);

  // ESCキーで閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeSettings();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeSettings]);

  // 統計リセット確認
  const handleResetStats = useCallback(() => {
    if (confirm('統計をリセットしますか？この操作は取り消せません。')) {
      resetStats();
    }
  }, [resetStats]);

  // 設定をデフォルトにリセット
  const handleResetToDefaults = useCallback(() => {
    if (confirm('すべての設定をデフォルトに戻しますか？')) {
      resetToDefaults();
    }
  }, [resetToDefaults]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="SettingsModal__overlay" onClick={closeSettings}>
      <div className="SettingsModal__content" onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <header className="SettingsModal__header">
          <h2 className="SettingsModal__title">設定</h2>
          <button
            className="SettingsModal__close"
            onClick={closeSettings}
            aria-label="閉じる"
          >
            ×
          </button>
        </header>

        {/* コンテンツ */}
        <div className="SettingsModal__body">
          {/* プレイヤー設定 */}
          <section className="SettingsModal__section">
            <h3 className="SettingsModal__section-title">プレイヤー設定</h3>
            <div className="SettingsModal__field">
              <label className="SettingsModal__label" htmlFor="playerName">
                プレイヤー名
              </label>
              <input
                id="playerName"
                type="text"
                className="SettingsModal__input"
                value={player.name}
                onChange={(e) => updatePlayerName(e.target.value)}
                maxLength={20}
              />
            </div>
            <div className="SettingsModal__stats">
              <div className="SettingsModal__stat">
                <span className="SettingsModal__stat-label">プレイ回数</span>
                <span className="SettingsModal__stat-value">{player.stats.gamesPlayed}</span>
              </div>
              <div className="SettingsModal__stat">
                <span className="SettingsModal__stat-label">勝利数</span>
                <span className="SettingsModal__stat-value">{player.stats.gamesWon}</span>
              </div>
              <div className="SettingsModal__stat">
                <span className="SettingsModal__stat-label">勝率</span>
                <span className="SettingsModal__stat-value">{player.stats.winRate.toFixed(1)}%</span>
              </div>
              <button
                className="SettingsModal__button SettingsModal__button--small"
                onClick={handleResetStats}
              >
                統計リセット
              </button>
            </div>
          </section>

          {/* オーディオ設定 */}
          <section className="SettingsModal__section">
            <h3 className="SettingsModal__section-title">オーディオ</h3>
            <div className="SettingsModal__field">
              <label className="SettingsModal__label" htmlFor="masterVolume">
                マスター音量: {audio.masterVolume}%
              </label>
              <input
                id="masterVolume"
                type="range"
                className="SettingsModal__slider"
                min="0"
                max="100"
                value={audio.masterVolume}
                onChange={(e) => updateAudio({ masterVolume: Number(e.target.value) })}
              />
            </div>
            {/* プロトタイプ版ではSE関連コントロールを非表示
                ref: https://team-wanted.atlassian.net/wiki/x/AYA3Cw
            <div className="SettingsModal__field">
              <label className="SettingsModal__label" htmlFor="soundEffectsVolume">
                効果音: {audio.soundEffectsVolume}%
              </label>
              <input
                id="soundEffectsVolume"
                type="range"
                className="SettingsModal__slider"
                min="0"
                max="100"
                value={audio.soundEffectsVolume}
                onChange={(e) => updateAudio({ soundEffectsVolume: Number(e.target.value) })}
              />
            </div>
            <div className="SettingsModal__field SettingsModal__field--row">
              <label className="SettingsModal__label" htmlFor="soundEnabled">
                効果音を有効
              </label>
              <input
                id="soundEnabled"
                type="checkbox"
                className="SettingsModal__checkbox"
                checked={audio.soundEnabled}
                onChange={(e) => updateAudio({ soundEnabled: e.target.checked })}
              />
            </div>
            */}
            <div className="SettingsModal__field SettingsModal__field--row">
              <label className="SettingsModal__label" htmlFor="musicEnabled">
                BGMを有効
              </label>
              <input
                id="musicEnabled"
                type="checkbox"
                className="SettingsModal__checkbox"
                checked={audio.musicEnabled}
                onChange={(e) => updateAudio({ musicEnabled: e.target.checked })}
              />
            </div>
          </section>

          {/* ビジュアル設定 */}
          <section className="SettingsModal__section">
            <h3 className="SettingsModal__section-title">ビジュアル</h3>
            <div className="SettingsModal__field">
              <label className="SettingsModal__label" htmlFor="animationSpeed">
                アニメーション速度
              </label>
              <select
                id="animationSpeed"
                className="SettingsModal__select"
                value={visual.animationSpeed}
                onChange={(e) => updateVisual({ animationSpeed: e.target.value as 'slow' | 'normal' | 'fast' })}
              >
                <option value="slow">遅い</option>
                <option value="normal">普通</option>
                <option value="fast">速い</option>
              </select>
            </div>
            <div className="SettingsModal__field SettingsModal__field--row">
              <label className="SettingsModal__label" htmlFor="animationsEnabled">
                アニメーションを有効
              </label>
              <input
                id="animationsEnabled"
                type="checkbox"
                className="SettingsModal__checkbox"
                checked={visual.animationsEnabled}
                onChange={(e) => updateVisual({ animationsEnabled: e.target.checked })}
              />
            </div>
            <div className="SettingsModal__field SettingsModal__field--row">
              <label className="SettingsModal__label" htmlFor="particleEffects">
                パーティクルエフェクト
              </label>
              <input
                id="particleEffects"
                type="checkbox"
                className="SettingsModal__checkbox"
                checked={visual.particleEffects}
                onChange={(e) => updateVisual({ particleEffects: e.target.checked })}
              />
            </div>
          </section>

          {/* ゲームルール設定 */}
          <section className="SettingsModal__section">
            <h3 className="SettingsModal__section-title">ゲームルール</h3>
            <div className="SettingsModal__field">
              <label className="SettingsModal__label" htmlFor="aiDifficulty">
                AI難易度
              </label>
              <select
                id="aiDifficulty"
                className="SettingsModal__select"
                value={rules.aiDifficulty}
                onChange={(e) => updateRules({ aiDifficulty: e.target.value as 'easy' | 'medium' | 'hard' })}
              >
                <option value="easy">イージー</option>
                <option value="medium">ノーマル</option>
                <option value="hard">ハード</option>
              </select>
            </div>
            <div className="SettingsModal__field SettingsModal__field--row">
              <label className="SettingsModal__label" htmlFor="allowDrawStacking">
                ドロースタック
              </label>
              <input
                id="allowDrawStacking"
                type="checkbox"
                className="SettingsModal__checkbox"
                checked={rules.allowDrawStacking}
                onChange={(e) => updateRules({ allowDrawStacking: e.target.checked })}
              />
            </div>
            <div className="SettingsModal__field SettingsModal__field--row">
              <label className="SettingsModal__label" htmlFor="allowJumpIn">
                ジャンプイン
              </label>
              <input
                id="allowJumpIn"
                type="checkbox"
                className="SettingsModal__checkbox"
                checked={rules.allowJumpIn}
                onChange={(e) => updateRules({ allowJumpIn: e.target.checked })}
              />
            </div>
          </section>
        </div>

        {/* フッター */}
        <footer className="SettingsModal__footer">
          <button
            className="SettingsModal__button SettingsModal__button--secondary"
            onClick={handleResetToDefaults}
          >
            デフォルトに戻す
          </button>
          <button
            className="SettingsModal__button SettingsModal__button--primary"
            onClick={closeSettings}
          >
            閉じる
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SettingsModal;
